import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  InView,
  EyebrowLabel,
  InlineCta,
} from "../utils/Animation";
import NotFound from "../pages/404";
import {
  PRODUCT_DATA_MAP,
  PRODUCTS_REGISTRY,
  type ProductData,
  type ProductCard,
} from "../data/products/productsRegistry";



// ─── Status badge colours ─────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  Live: "bg-green-50 text-green-700 border-green-200",
  Beta: "bg-amber-50 text-amber-700 border-amber-200",
  "In Development": "bg-blue-50 text-blue-700 border-blue-200",
};

// ─── Related products ─────────────────────────────────────────────────
// Derived from the registry — exclude current product, take the next 2.
function getRelated(currentSlug: string): ProductCard[] {
  return PRODUCTS_REGISTRY.filter((p) => p.slug !== currentSlug).slice(0, 2);
}

// ─── Page ─────────────────────────────────────────────────────────────
export default function SingleProduct() {
  const { slug } = useParams<{ slug: string }>();
  const product: ProductData | undefined = slug
    ? PRODUCT_DATA_MAP[slug]
    : undefined;
  const [activeScreenshot, setActiveScreenshot] = useState(0);

  if (!product) {
    return (
      <NotFound
        heading="Product not found."
        message="This product page may have moved or been updated."
        backLabel="Back to products"
        backHref="/products"
      />
    );
  }

  const related = getRelated(product.slug);

  return (
    <>
      {/* ── Hero ── */}
      <div className="relative bg-itl-navy pt-40 pb-32 overflow-hidden">
        <img
          src={product.heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-itl-navy/60 to-itl-navy" />
        <div className="absolute left-6 lg:left-12 top-0 bottom-0 flex items-center pointer-events-none">
          <div className="h-[35%] w-px bg-white/10" />
        </div>

        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 mb-8"
          >
            <Link
              to="/products"
              className="text-[11px] uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors"
            >
              Products
            </Link>
            <span className="text-white/20 text-xs">/</span>
            <span className="text-[11px] uppercase tracking-widest text-itl-blue">
              {product.title}
            </span>
          </motion.div>

          <div className="flex flex-wrap items-center gap-4 mb-6">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue"
            >
              {product.tag}
            </motion.span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              className={`px-3 py-1 border text-[10px] font-semibold uppercase tracking-widest rounded-sm ${STATUS_COLORS[product.status] ?? ""}`}
            >
              {product.status}
            </motion.span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.85,
              ease: [0.22, 1, 0.36, 1],
              delay: 0.3,
            }}
            className="font-serif text-5xl lg:text-7xl font-light text-white max-w-3xl leading-[1.06] mb-6"
          >
            {product.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-white/55 text-lg max-w-xl leading-relaxed mb-10"
          >
            {product.tagline}
          </motion.p>

          {product.demoUrl && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.65 }}
            >
              <a
                href={product.demoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-4 bg-itl-blue text-white text-[12px] font-semibold uppercase tracking-widest hover:bg-blue-600 transition-colors"
              >
                View Live Demo →
              </a>
            </motion.div>
          )}
        </div>
      </div>

      {/* ── Metrics bar ── */}
      {product.metrics && product.metrics.length > 0 && (
        <section className="bg-[#f7f7f5] border-b border-gray-200">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
              {product.metrics.map((m, i) => (
                <InView key={m.label} delay={i * 0.07}>
                  <div className="py-10 px-8 text-center">
                    <p className="font-serif text-4xl font-light text-itl-navy mb-1">
                      {m.value}
                    </p>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                      {m.label}
                    </p>
                  </div>
                </InView>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Overview ── */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-16">
          <InView>
            <EyebrowLabel>About This Product</EyebrowLabel>
            <h2 className="font-serif text-3xl font-light text-itl-navy leading-snug">
              The problem it solves, and how.
            </h2>
          </InView>
          <InView delay={0.1}>
            <div className="flex flex-col gap-5">
              {product.overview.map((para, i) => (
                <p
                  key={i}
                  className="text-gray-600 leading-relaxed text-[15px]"
                >
                  {para}
                </p>
              ))}
            </div>
          </InView>
        </div>
      </section>

      {/* ── Screenshots ── */}
      {product.screenshots.length > 0 && (
        <section className="bg-[#f7f7f5] py-24">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <InView className="mb-10">
              <EyebrowLabel>Screenshots</EyebrowLabel>
              <h2 className="font-serif text-3xl font-light text-itl-navy leading-snug">
                See it in detail.
              </h2>
            </InView>
            <InView delay={0.05}>
              {/* Main screenshot */}
              <div className="relative aspect-video bg-gray-100 overflow-hidden mb-4">
                <motion.img
                  key={activeScreenshot}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  src={product.screenshots[activeScreenshot].image}
                  alt={product.screenshots[activeScreenshot].label}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-itl-navy/80 px-3 py-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-white">
                    {product.screenshots[activeScreenshot].label}
                  </span>
                </div>
              </div>
              {/* Thumbnail strip */}
              <div className="flex gap-2">
                {product.screenshots.map((s, i) => (
                  <button
                    key={s.label}
                    onClick={() => setActiveScreenshot(i)}
                    className={`relative flex-1 aspect-video overflow-hidden transition-all duration-200 ${
                      activeScreenshot === i
                        ? "ring-2 ring-itl-blue"
                        : "opacity-50 hover:opacity-80"
                    }`}
                  >
                    <img
                      src={s.image}
                      alt={s.label}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </InView>
          </div>
        </section>
      )}

      {/* ── Key features ── */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-14">
            <EyebrowLabel>Key Features</EyebrowLabel>
            <h2 className="font-serif text-4xl font-light text-itl-navy max-w-lg leading-snug">
              Built to solve real problems, not check boxes.
            </h2>
          </InView>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100">
            {product.keyFeatures.map((f, i) => (
              <InView key={f.title} delay={i * 0.06} className="bg-white p-8">
                <span className="text-3xl mb-5 block">{f.icon}</span>
                <h3 className="font-serif text-xl font-light text-itl-navy mb-3">
                  {f.title}
                </h3>
                <p className="text-gray-500 text-[13px] leading-relaxed">
                  {f.body}
                </p>
              </InView>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tech stack ── */}
      <section className="bg-itl-navy py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-16">
          <InView>
            <EyebrowLabel>Under the Hood</EyebrowLabel>
            <h2 className="font-serif text-3xl font-light text-white leading-snug">
              Technology stack, layer by layer.
            </h2>
          </InView>
          <InView delay={0.1}>
            <div className="flex flex-col divide-y divide-white/10">
              {product.techStack.map((t) => (
                <div
                  key={t.layer}
                  className="py-4 flex items-center justify-between gap-4"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-white/40 w-32 shrink-0">
                    {t.layer}
                  </span>
                  <span className="text-white/90 text-[14px]">{t.tech}</span>
                </div>
              ))}
            </div>
          </InView>
        </div>
      </section>

      {/* ── Related products (auto-populated from registry) ── */}
      {related.length > 0 && (
        <section className="bg-[#f7f7f5] py-16">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <InView className="mb-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
                More Products
              </p>
            </InView>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200">
              {related.map((r, i) => (
                <InView
                  key={r.slug}
                  delay={i * 0.08}
                  className="bg-white group"
                >
                  <Link
                    to={r.href}
                    className="flex items-center justify-between p-8"
                  >
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-itl-blue mb-2">
                        {r.tag}
                      </p>
                      <h3 className="font-serif text-2xl font-light text-itl-navy group-hover:text-itl-blue transition-colors">
                        {r.title}
                      </h3>
                    </div>
                    <span className="text-itl-blue text-xl group-hover:translate-x-1 transition-transform">
                      →
                    </span>
                  </Link>
                </InView>
              ))}
            </div>
          </div>
        </section>
      )}

      <InlineCta
        heading="Want us to build something like this for you?"
        body="Tell us your requirements and we'll come back with a clear approach and realistic timeline."
        ctaLabel="Start a Conversation"
        ctaHref="/contact"
      />
    </>
  );
}
